import { useMemo } from "react";
import { useAuthSelector } from "../store/auth";

const useAuth = () => {
  const { user } = useAuthSelector();
  const token = localStorage.getItem("accessToken");
  const isAuthenticated = !!token;

  // Prefer the `permissions` array from /api/user/me/; fall back to is_staff/is_superuser
  // flags for backwards-compatibility with cached sessions that predate the permissions field.
  const permissions: string[] = useMemo(() => {
    if (!user) return [];
    if (Array.isArray(user.permissions) && user.permissions.length > 0) return user.permissions;
    // Legacy fallback: synthesise permissions from staff/superuser flags
    if (user.is_superuser) return ["create", "delete", "manage_restrictions", "manage_users", "read", "update"];
    if (user.is_staff) return ["create", "read", "update"];
    return ["read"];
  }, [user]);

  const canCreate = useMemo(() => permissions.includes("create"), [permissions]);
  const canUpdate = useMemo(() => permissions.includes("update"), [permissions]);
  const canDelete = useMemo(() => permissions.includes("delete"), [permissions]);
  const canManageRestrictions = useMemo(() => permissions.includes("manage_restrictions"), [permissions]);
  const canManageUsers = useMemo(() => permissions.includes("manage_users"), [permissions]);

  /** True for any user who can write (Editor or Admin) */
  const hasWritePermissions = useMemo(() => canCreate || canUpdate || canDelete, [canCreate, canUpdate, canDelete]);

  return { isAuthenticated, hasWritePermissions, canCreate, canUpdate, canDelete, canManageRestrictions, canManageUsers } as const;
};

export default useAuth;
